import React, { useState } from 'react';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import { GenericModal } from 'src/components/Modals/GenericModal';
import { SlButton } from 'src/components/BeaconV2Components/Header/SLDropdownMenu/SlButton';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import { sendCreditRequestEmail } from 'src/utils/creditsApi';
import { useSnackbar } from 'src/utils/Hooks';

export type WarningModalVariant = 'noActiveContracts' | 'noCreditBalance';

interface WarningModalProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  variant: WarningModalVariant;
  featureName?: string;
}

const getModalContent = (variant: WarningModalVariant, featureName: string): { title: string; body: string } =>
  ({
    noActiveContracts: {
      title: 'No active contracts',
      body: `There are currently no active contracts. Contact customer support to continue using ${featureName}.`
    },
    noCreditBalance: {
      title: 'No credit balance',
      body: `You've used all available credits. Contact customer support to continue using ${featureName}.`
    }
  }[variant]);

const WarningModal = ({ isOpen, setIsOpen, variant, featureName = 'Presentations' }: WarningModalProps) => {
  const { title, body } = getModalContent(variant, featureName);
  const { showSnackbar } = useSnackbar();
  const [isSending, setIsSending] = useState(false);

  const handleClose = () => setIsOpen(false);

  const handleContact = async () => {
    setIsSending(true);
    try {
      await sendCreditRequestEmail(featureName);
      showSnackbar({
        message: 'We submitted your request to customer support. Our team will contact you soon.',
        type: 'success'
      });
      handleClose();
    } catch {
      showSnackbar({
        message: 'Failed to send request. Please try again or contact support@stackline.com.',
        type: 'error'
      });
    } finally {
      setIsSending(false);
    }
  };

  return (
    <GenericModal
      open={isOpen}
      onClose={handleClose}
      sx={{
        '& .MuiPaper-root': {
          width: '500px',
          padding: '24px'
        },
        '.genericModal__header': {
          paddingBottom: '12px'
        },
        '.genericModal__footer': {
          borderTop: 'none',
          position: 'relative',
          padding: '20px 0 0'
        }
      }}
    >
      <GenericModal.Header title={title} onClose={handleClose} />
      <GenericModal.Body>
        <Text variant="body2" lineHeight="20px">
          {body}
        </Text>
      </GenericModal.Body>
      <GenericModal.Footer>
        <Flex justifyContent="flex-end" gap={16}>
          <SlButton onClick={handleClose}>Cancel</SlButton>
          <SlButton variant="contained" onClick={handleContact} disabled={isSending}>
            {isSending ? 'Sending...' : 'Contact'}
          </SlButton>
        </Flex>
      </GenericModal.Footer>
    </GenericModal>
  );
};

export default WarningModal;
